import { AnimatePresence, motion } from "framer-motion";
import { FiShoppingBag } from "react-icons/fi";

import useStore from "../../hooks/use-store";
import Product from "./Product";

interface Props {
  className?: string;
}

// ─── Skeleton Card ─────────────────────────────────────────────────────────────

const SkeletonCard = ({ delay }: { delay: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.12 } }}
    transition={{ delay, type: "spring", stiffness: 300, damping: 28 }}
    className="relative overflow-hidden bg-white/[0.04] rounded-sm"
  >
    {/** Shimmer sweep — translating a div is GPU-composited, no layout thrash */}
    <motion.div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.055) 50%, transparent 100%)",
      }}
      animate={{ x: ["-100%", "100%"] }}
      transition={{
        duration: 1.7,
        repeat: Infinity,
        ease: "linear",
        delay: delay * 0.4,
      }}
    />

    {/** Fake product row */}
    <div className="p-2 flex items-center space-x-2 bg-black/70 rounded-md h-[52px]">
      <div className="w-[44px] h-[37px] bg-white/[0.05] rounded-sm shrink-0" />
      <div className="flex flex-col gap-[7px] flex-1">
        <div className="h-[10px] w-[52%] bg-white/[0.05] rounded-sm" />
        <div className="h-[9px] w-[20%] bg-[#9EFF23]/[0.07] rounded-sm" />
      </div>
    </div>
  </motion.div>
);

// ─── Empty State ───────────────────────────────────────────────────────────────

const EmptyState = () => (
  <motion.div
    key="empty"
    initial={{ opacity: 0, scale: 0.93 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.93 }}
    transition={{ type: "spring", stiffness: 280, damping: 24 }}
    className="flex flex-col items-center justify-center py-12 gap-3"
  >
    <motion.div
      animate={{ y: [0, -5, 0] }}
      transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
      className="text-white/[0.12]"
    >
      <FiShoppingBag size={32} />
    </motion.div>
    <p className="text-white/[0.2] text-[.82rem] gilroy-font tracking-wide">
      No products available
    </p>
  </motion.div>
);

// ─── ClothingList ──────────────────────────────────────────────────────────────

const ClothingList = ({ className = "" }: Props) => {
  const { products, loading } = useStore();

  const isEmpty = !loading && (!products || products.length === 0);

  return (
    /**
     * mode="wait" ensures skeleton fully exits before the list enters —
     * prevents both states being visible simultaneously during the swap.
     */
    <AnimatePresence mode="wait">
      {/** ── Loading Skeleton ─────────────────────────────────────────────── */}
      {loading && (
        <motion.div
          key="skeleton"
          className={`space-y-3 overflow-y-auto scrollbar-1 pr-2 ${className}`}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonCard key={i} delay={i * 0.055} />
          ))}
        </motion.div>
      )}

      {/** ── Empty State ──────────────────────────────────────────────────── */}
      {isEmpty && <EmptyState key="empty" />}

      {/** ── Products List ────────────────────────────────────────────────── */}
      {!loading && !!products?.length && (
        <motion.div
          key="list"
          className={`space-y-2 overflow-y-auto scrollbar-1 pr-2 ${className}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, transition: { duration: 0.14 } }}
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          {products.map((product, i) => (
            <Product
              key={product.id}
              {...product}
              imgSrc={product.imgSrc}
              canEdit={i % 2 === 0}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ClothingList;
